import type {PlaceDetails} from '@Components/google-map/google-map.types';
import {USER_LOCATION_TIMEOUT_MS} from '@Components/google-map/google-map.types';
import {DEFAULT_LATITUDE, DEFAULT_LONGITUDE} from '@Components/google-map/google-map.types';
import {DEFAULT_LOCATION_ADDRESS} from '@Components/google-map/google-map.types';
import LatLngLiteral = google.maps.LatLngLiteral;
import GeocoderResult = google.maps.GeocoderResult;

export const getUserCurrentLocation = (onLocationFetched: (place: PlaceDetails | undefined) => void): void => {
  if (!navigator.geolocation) {
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (position: GeolocationPosition) => {
      void onUserLocationFetched(position, onLocationFetched);
    },
    null,
    {
      enableHighAccuracy: true,
      timeout: USER_LOCATION_TIMEOUT_MS,
    }
  );
};

const onUserLocationFetched = async (position: GeolocationPosition, onLocationFetched: (place: PlaceDetails | undefined) => void): Promise<void> => {
  const place = await getPlaceAddressByCords(position.coords);
  onLocationFetched(place);
};

export async function getPlaceAddressByCords(location: GeolocationCoordinates): Promise<PlaceDetails | undefined> {
  await window.google.maps.importLibrary('geocoding');
  const geocoder = new window.google.maps.Geocoder();
  const coords: LatLngLiteral = {lat: location.latitude, lng: location.longitude};

  return geocoder.geocode({location: coords}).then((response) => {
    if (response.results.length === 0) {
      return undefined;
    }
    return extractPlaceDetailsFromGeocodingResult(response.results[0]);
  });
}

const extractPlaceDetailsFromGeocodingResult = (result: GeocoderResult): PlaceDetails => {
  const addressComponents = getAddressComponents(result.address_components);

  return {
    name: result.address_components[0]?.short_name ?? addressComponents.city,
    placeID: result.place_id,
    address: result.formatted_address ?? undefined,
    longitude: result.geometry?.location?.lng() ?? 0,
    latitude: result.geometry?.location?.lat() ?? 0,
    ...addressComponents,
  };
};

export const extractPlaceDetailsFromAutocompletePlaceResult = (
  autocompletePrediction: google.maps.places.AutocompletePrediction,
  placeResult: google.maps.places.PlaceResult
): PlaceDetails => {
  const addressComponents = placeResult.address_components ? getAddressComponents(placeResult.address_components) : undefined;

  return {
    name: autocompletePrediction.structured_formatting.main_text ?? undefined,
    placeID: autocompletePrediction.place_id,
    address: placeResult.formatted_address ?? undefined,
    longitude: placeResult.geometry?.location?.lng() ?? 0,
    latitude: placeResult.geometry?.location?.lat() ?? 0,
    ...addressComponents,
  };
};

export const getGoogleMapEmbedSrcLocationFromVenue = (venue: PlaceDetails | undefined, apiKey: string): string => {
  if (!venue) {
    return '';
  }
  const searchParam = getGoogleMapEmbedSearchParam(venue);
  const center = venue ? `${venue.latitude},${venue.longitude}` : `${DEFAULT_LATITUDE},${DEFAULT_LONGITUDE}`;

  return `https://maps.google.com/maps/embed/v1/place?key=${apiKey}&q=${searchParam}&center=${center}`;
};

export const updateGoogleMapEmbedSrcLocation = (src: string | null, venue: PlaceDetails): string => {
  if (!src) {
    return '';
  }
  const urlObj = new URL(src);
  const params = new URLSearchParams(urlObj.search);
  params.set('q', getGoogleMapEmbedSearchParam(venue));
  urlObj.search = params.toString();
  return urlObj.toString();
};

const getGoogleMapEmbedSearchParam = (venue: PlaceDetails | undefined): string => {
  let searchParam = DEFAULT_LOCATION_ADDRESS;

  if (venue) {
    searchParam = venue.placeID ? `place_id:${venue.placeID}` : (venue.address ?? '');
  }
  return encodeURI(searchParam);
};

const isGeocoderAddressComponent = (component: google.maps.GeocoderAddressComponent | google.maps.places.AddressComponent): component is google.maps.GeocoderAddressComponent => {
  return (component as google.maps.GeocoderAddressComponent).long_name !== undefined;
};

const getAddressComponents = (
  addressComponents: (google.maps.GeocoderAddressComponent | google.maps.places.AddressComponent)[]
): {country: string; province: string; city: string; postalCode: string} => {
  const details = {
    country: '',
    province: '',
    city: '',
    postalCode: '',
  };

  for (const component of addressComponents) {
    const componentType = component.types[0];

    const componentName = isGeocoderAddressComponent(component) ? component.long_name : component.longText;

    switch (componentType) {
      case 'country':
        details.country = componentName ?? '';
        break;
      case 'administrative_area_level_1':
        details.province = componentName ?? '';
        break;
      case 'locality':
        details.city = componentName ?? '';
        break;
      case 'postal_code':
        details.postalCode = componentName ?? '';
        break;
      default:
        break;
    }
  }

  return details;
};
